import React from 'react';
import PropTypes from 'prop-types';

import Button from 'ui/Button';
import Description from 'ui/text/Description';
import Subtitle from 'ui/text/Subtitle';

import { amountOfProductInCart, sameCartLine, useCart } from 'utils/CartContext';
import { useError } from 'utils/useErrorController';
import { convertPriceFromCents, currencySymbol } from 'utils/rewards';
import { calculateLineMaxAmount } from 'utils/calculateProductMaxAmount';

import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';

import { t } from "i18n/index";

const AddToCartButton = ({ product, compact, variant }) => {
  const { cartItems, addToCart, removeFromCart, updateQuantity } = useCart();
  const { loading, handleError } = useError();

  const { available, display_price, currency, display_discount_price, redeem_limit, redeemed_count } = product;

  const hasVariants = product.variants?.length > 0;
  // For variant products, availability and stock come from the selected SKU.
  const lineAvailable = hasVariants ? !!variant?.available : available;
  const redeemedMax = redeem_limit && redeemed_count >= redeem_limit;

  const cartItem = cartItems.find((item) => sameCartLine(item, product, variant?.id));
  const inCart = !!cartItem;

  // Other SKU lines of this product consume its shared redeem allowance.
  const heldElsewhere = amountOfProductInCart(cartItems, product, variant?.id);
  const maxAmount = calculateLineMaxAmount(product, variant, heldElsewhere);
  const allowanceExhausted = !inCart && maxAmount < 1;

  const awaitingVariant = hasVariants && !variant;
  const disabled = redeemedMax || !lineAvailable || awaitingVariant || allowanceExhausted;

  // "Sold out" and "Limit reached" are states of this action. A pending SKU
  // choice is not — the picker above already asks for it, so the button keeps
  // its name and just waits, disabled, rather than parroting the placeholder.
  // An unchosen SKU can't be sold out, so fall back to the product's aggregate.
  const availableForLabel = awaitingVariant ? available : lineAvailable;
  const label = !availableForLabel
    ? t('literal:soldOut')
    : allowanceExhausted
      ? t('literal:limitReached')
      : t('literal:addToCart');

  const convertedPrice = convertPriceFromCents(display_price);
  const priceWithCurrency = `${parseFloat(convertedPrice)} ${currencySymbol[currency]}`;

  const convertedDiscountPrice = display_discount_price && convertPriceFromCents(display_discount_price);
  const discountPriceWithCurrency = display_discount_price && `${parseFloat(convertedDiscountPrice)} ${currencySymbol[currency]}`;

  const handleAddToCart = () => {
    const result = addToCart(product, variant);
    if (!result.ok && result.reason === 'event_mismatch') {
      handleError(t('errors:cartEventMismatch'));
    }
    if (!result.ok && result.reason === 'variant_required') {
      handleError(t('errors:pickAnOption'));
    }
    if (!result.ok && result.reason === 'redeem_limit_reached') {
      handleError(t('errors:redeemedMax'));
    }
  };

  if (inCart) {
    return (
      <div className={compact ? "add-to-cart-quantity" : "add-to-cart-wrapper"}>
        {!compact ? (
          <div className="add-to-cart-quantity">
            <IconButton
              size="small"
              onClick={() => {
                if (cartItem.amount <= 1) {
                  removeFromCart(product.id, variant?.id);
                } else {
                  updateQuantity(product.id, cartItem.amount - 1, variant?.id);
                }
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            <Description bold>{cartItem.amount}</Description>

            <IconButton
              size="small"
              onClick={() => updateQuantity(product.id, cartItem.amount + 1, variant?.id)}
              disabled={cartItem.amount >= maxAmount}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </div>
        ) : (
          <>
            <IconButton
              size="small"
              onClick={() => {
                if (cartItem.amount <= 1) {
                  removeFromCart(product.id, variant?.id);
                } else {
                  updateQuantity(product.id, cartItem.amount - 1, variant?.id);
                }
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            <Description bold>{cartItem.amount}</Description>

            <IconButton
              size="small"
              onClick={() => updateQuantity(product.id, cartItem.amount + 1, variant?.id)}
              disabled={cartItem.amount >= maxAmount}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </div>
    );
  }

  if (compact) {
    return (
      <Button
        onClick={handleAddToCart}
        highlighted
        smallPadded
        loading={loading}
        disabled={disabled}
      >
        {label}
      </Button>
    );
  }

  return (
    <div className="add-to-cart-wrapper">
      <div className="add-to-cart-price">
        <Subtitle small struck={discountPriceWithCurrency}>
          {priceWithCurrency}
        </Subtitle>
        {discountPriceWithCurrency && <Subtitle small>{discountPriceWithCurrency}</Subtitle>}
      </div>

      <Button
        onClick={handleAddToCart}
        highlighted
        fullwidth
        smallPadded
        loading={loading}
        disabled={disabled}
      >
        {label}
      </Button>
    </div>
  );
};

AddToCartButton.propTypes = {
  product: PropTypes.object.isRequired,
  compact: PropTypes.bool,
  variant: PropTypes.object,
};

export default AddToCartButton;
